import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { LanguageProvider } from "../context/LanguageProvider";
import RequireAuth from "./guards/RequireAuth";

// Public pages
import Home from "../features/home/pages/Home";
import Rules from "../features/rules/pages/Rules";
import NotFound from "../features/not-found/NotFound";
import BlogListPage from "../features/blog/pages/BlogListPage";
import BlogPostPage from "../features/blog/pages/BlogPostPage";
import CourseListPage from "../features/courses/pages/CourseListPage";
import CourseDetailPage from "../features/courses/pages/CourseDetailPage";

// Account pages
import AuthPage from "../features/auth/pages/AuthPage";
import MyCoursesPage from "../features/account/pages/MyCoursesPage";
import PurchaseCoursePage from "../features/courses/pages/PurchaseCoursePage";
import CourseStructurePage from "../features/learn/pages/CourseStructurePage";
import ContentPage from "../features/learn/pages/ContentPage";
import CheatsheetListPage from "../features/cheatsheet/pages/CheatsheetListPage";
import CheatsheetPage from "../features/cheatsheet/pages/CheatsheetPage";

// The whole admin panel is one chunk, downloaded only when someone opens /admin
const AdminApp = lazy(() => import("../features/admin/AdminApp"));

const SUPPORTED_LANGUAGES = ["fa", "en"];
const DEFAULT_LANGUAGE = "fa";

// The language comes from the URL (/fa, /en): LanguageProvider reads it from there
const LanguageLayout = () => (
    <LanguageProvider>
        <Outlet />
    </LanguageProvider>
);

// The routes of the public site and the user area. They are written once and used under
// every language prefix. (A plain function, not a component: <Routes> only accepts <Route>
// elements as children.)
const publicRoutes = () => (
    <>
        <Route index element={<Home />} />
        <Route path="auth" element={<AuthPage />} />
        <Route path="rules" element={<Rules />} />
        <Route path="not-found" element={<NotFound />} />

        <Route path="blog" element={<BlogListPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />

        <Route path="course" element={<CourseListPage />} />
        <Route path="course/:course_id" element={<CourseDetailPage />} />

        {/* Logged-in user area: visitors without a session are sent to the login page */}
        <Route element={<RequireAuth />}>
            <Route path="account/course" element={<MyCoursesPage />} />
            <Route path="account/course/purchase/:course_id" element={<PurchaseCoursePage />} />
            <Route path="account/course/:course_id/content" element={<CourseStructurePage />} />
            <Route path="account/course/:course_id/content/:content_id" element={<ContentPage />} />
            <Route path="account/course/:course_id/cheatsheet" element={<CheatsheetListPage />} />
            <Route
                path="account/course/:course_id/cheatsheet/:cheatsheet_id"
                element={<CheatsheetPage />}
            />
        </Route>

        {/* Any other path under a valid language */}
        <Route path="*" element={<NotFound />} />
    </>
);

const AppRoutes = () => {
    return (
        <Routes>
            {/* Redirect root to the default language */}
            <Route path="/" element={<Navigate to={`/${DEFAULT_LANGUAGE}`} replace />} />

            {/* Admin panel: lazy loaded, with its own routes */}
            <Route
                path="/admin/*"
                element={
                    <Suspense fallback={<p className="p-10 text-center">Loading...</p>}>
                        <AdminApp />
                    </Suspense>
                }
            />

            {/* Public site: one fixed prefix per language, so a word like "admin" is never read as a language */}
            {SUPPORTED_LANGUAGES.map((lang) => (
                <Route key={lang} path={`/${lang}`} element={<LanguageLayout />}>
                    {publicRoutes()}
                </Route>
            ))}

            {/* Anything else (/foo, /foo/blog...) goes to the 404 page of the default language */}
            <Route path="*" element={<Navigate to={`/${DEFAULT_LANGUAGE}/not-found`} replace />} />
        </Routes>
    );
};

export default AppRoutes;